import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  listTopSimilarity,
  type SectorSimilarityPair,
  type SimilarityScope,
} from '../../api/sectorTaxonomyApi';

// 탭 F — 섹터 유사도 히트맵.
// 권위: dashboard-api v1.6 GET /api/v1/sectors/taxonomy/similarity.
//       docs/ARCH_DATA.md §5.16 sector_similarity — scope 는 source | canonical.
// 백엔드는 SectorSimilarityPair[] (쌍 리스트) 를 반환한다. 매트릭스 축 (sectors)
// 은 클라이언트에서 pairs 로부터 unique id 를 추출해 정렬한다. display_name 별도
// 조회 없음 — id (원천 섹터명 또는 sec_* slug) 를 그대로 라벨로 사용.
// 색상: HSL 그라디언트 (0 = 흰색, 0.5 = 연한 파랑, 1 = 진한 파랑).
// 대형 매트릭스 (top=100 → 10,000 셀) 는 경고 배너와 함께 렌더.

const TOP_N_OPTIONS = [30, 50, 100] as const;
type TopN = (typeof TOP_N_OPTIONS)[number];

const SCOPE_OPTIONS: SimilarityScope[] = ['source', 'canonical'];
const SCOPE_LABEL: Record<SimilarityScope, string> = {
  source: '원천 섹터',
  canonical: '정규 섹터',
};

interface AxisSector {
  id: string;
  displayName: string;
}

export function SimilarityHeatmapTab(): JSX.Element {
  const [scope, setScope] = useState<SimilarityScope>('source');
  const [topN, setTopN] = useState<TopN>(50);
  const [drillSector, setDrillSector] = useState<string>('');
  const [computedAt, setComputedAt] = useState<string>(''); // "" = 최신

  const query = useQuery<SectorSimilarityPair[]>({
    queryKey: ['sector-similarity', scope, topN, drillSector, computedAt],
    queryFn: () =>
      listTopSimilarity({
        scope,
        top: topN,
        sector: drillSector.trim() || undefined,
        computedAt: computedAt.trim() || undefined,
      }),
    staleTime: 60_000,
  });

  const pairs = query.data ?? [];

  // 축 배열 — pairs 를 순회해 unique id 추출 후 정렬.
  // display_name 조회가 없으므로 id 자체를 label 로 사용 (canonical 의 sec_* slug 포함).
  const sectors = useMemo<AxisSector[]>(() => {
    const ids = new Set<string>();
    for (const p of pairs) {
      ids.add(p.sectorA);
      ids.add(p.sectorB);
    }
    return Array.from(ids)
      .sort()
      .map((id) => ({ id, displayName: id }));
  }, [pairs]);

  // 쌍 리스트 → {A|B → pair} 맵으로 정규화 (양방향 조회 지원).
  const pairMap = useMemo(() => {
    const m = new Map<string, SectorSimilarityPair>();
    for (const p of pairs) {
      m.set(`${p.sectorA}|${p.sectorB}`, p);
      m.set(`${p.sectorB}|${p.sectorA}`, p);
    }
    return m;
  }, [pairs]);

  // 응답 전체의 computed_at 표시용: pairs 중 가장 최근값.
  const latestComputedAt = useMemo(() => {
    if (pairs.length === 0) return null;
    return pairs.reduce<string>((acc, p) => (p.computedAt > acc ? p.computedAt : acc), '');
  }, [pairs]);

  const isDrill = drillSector.trim().length > 0;
  const willBeLarge = topN === 100;
  const isEmpty = !query.isLoading && !query.isError && pairs.length === 0;

  return (
    <div className="tx-heatmap">
      {/* 툴바 */}
      <div className="pca-filter">
        <div className="pca-filter__group">
          <label className="pca-filter__label">scope</label>
          <div className="pca-chip-row">
            {SCOPE_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={`pca-chip${scope === s ? ' pca-chip--active' : ''}`}
                onClick={() => setScope(s)}
              >
                {SCOPE_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">Top-N</label>
          <div className="pca-chip-row">
            {TOP_N_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                className={`pca-chip${topN === n ? ' pca-chip--active' : ''}`}
                onClick={() => setTopN(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="sh-sector">
            특정 섹터 drill-down
          </label>
          <input
            id="sh-sector"
            className="pca-input"
            type="text"
            placeholder={scope === 'canonical' ? 'sec_semiconductor' : '반도체'}
            value={drillSector}
            onChange={(e) => setDrillSector(e.target.value)}
            style={{ width: 180 }}
          />
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="sh-computed">
            computed_at (옵션)
          </label>
          <input
            id="sh-computed"
            className="pca-input"
            type="date"
            value={computedAt}
            onChange={(e) => setComputedAt(e.target.value)}
          />
        </div>
      </div>

      {willBeLarge && (
        <div className="tx-heatmap__warn">
          <AlertTriangle size={14} strokeWidth={1.75} />
          Top-100 은 100×100 = 10,000 셀입니다 — 렌더링 지연 가능. Top-50 이하
          권장.
        </div>
      )}

      {query.isError && (
        <div className="pca-warning-banner">
          유사도 매트릭스 로드 실패:{' '}
          {(query.error as Error).message ?? 'unknown'}
        </div>
      )}

      {query.isLoading && <div className="pca-muted">로딩 중…</div>}

      {isEmpty && (
        <div className="pca-muted">
          유사도 데이터 없음 — 스케줄러 실행 후 생성됩니다.
        </div>
      )}

      {!query.isLoading && !query.isError && pairs.length > 0 && (
        <>
          <div className="tx-heatmap__meta">
            computed_at: <code>{latestComputedAt?.slice(0, 16) ?? '—'}</code> ·
            sectors: {sectors.length} · pairs: {pairs.length}
          </div>

          {/* Legend */}
          <HeatmapLegend />

          {isDrill ? (
            <DrillDownList
              sectorId={drillSector.trim()}
              pairs={pairs}
            />
          ) : (
            <Matrix sectors={sectors} pairMap={pairMap} />
          )}
        </>
      )}
    </div>
  );
}

// ── Matrix 뷰 ─────────────────────────────────────────────────
interface MatrixProps {
  sectors: AxisSector[];
  pairMap: Map<string, SectorSimilarityPair>;
}

function Matrix({ sectors, pairMap }: MatrixProps): JSX.Element {
  // hover 툴팁 상태: portal 로 그린다 (table 의 overflow 클리핑 회피).
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    labelA: string;
    labelB: string;
    jaccard: number;
    intersection: number;
    union: number;
  } | null>(null);

  if (sectors.length === 0) {
    return <div className="pca-muted">섹터가 없습니다.</div>;
  }

  const n = sectors.length;
  const cellSize = n > 60 ? 10 : n > 40 ? 14 : 18;

  return (
    <div className="tx-heatmap__wrap">
      <div
        className="tx-heatmap__grid"
        style={{
          gridTemplateColumns: `120px repeat(${n}, ${cellSize}px)`,
          // 첫 행 = 120px (상단 세로 라벨 전용), 이후 행 = cellSize.
          // gridAutoRows 로만 주면 라벨 행까지 cellSize 로 클립되어 라벨이 히트맵 안으로 잘려 들어간다.
          gridTemplateRows: `120px repeat(${n}, ${cellSize}px)`,
        }}
      >
        {/* 좌상단 빈 코너 */}
        <div className="tx-heatmap__corner" />
        {/* 상단 라벨 */}
        {sectors.map((s) => (
          <div
            key={`col-${s.id}`}
            className="tx-heatmap__col-label"
            title={s.id}
          >
            <span>{s.displayName}</span>
          </div>
        ))}
        {/* 본문 */}
        {sectors.map((rowSec) => (
          <Row
            key={`row-${rowSec.id}`}
            rowSec={rowSec}
            sectors={sectors}
            pairMap={pairMap}
            cellSize={cellSize}
            onHoverCell={setHover}
          />
        ))}
      </div>

      {hover &&
        createPortal(
          <div
            className="tx-heatmap__tip"
            style={{ top: hover.y + 12, left: hover.x + 12 }}
          >
            <div className="tx-heatmap__tip-title">
              <strong>{hover.labelA}</strong> <span style={{ opacity: 0.6 }}>×</span>{' '}
              <strong>{hover.labelB}</strong>
            </div>
            <div className="tx-heatmap__tip-row">
              jaccard: <code>{hover.jaccard.toFixed(3)}</code>
            </div>
            <div className="tx-heatmap__tip-row">
              ∩: <code>{hover.intersection}</code> / ∪: <code>{hover.union}</code>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface RowProps {
  rowSec: AxisSector;
  sectors: AxisSector[];
  pairMap: MatrixProps['pairMap'];
  cellSize: number;
  onHoverCell: (
    h: {
      x: number;
      y: number;
      labelA: string;
      labelB: string;
      jaccard: number;
      intersection: number;
      union: number;
    } | null,
  ) => void;
}

// Row 는 React.memo 대상이 되는 좋은 경계지만, 단위가 작아 매 렌더 허용.
function Row({ rowSec, sectors, pairMap, cellSize, onHoverCell }: RowProps): JSX.Element {
  void cellSize;
  return (
    <>
      <div
        className="tx-heatmap__row-label"
        title={rowSec.id}
      >
        <span>{rowSec.displayName}</span>
      </div>
      {sectors.map((colSec) => {
        if (colSec.id === rowSec.id) {
          return (
            <div
              key={`${rowSec.id}-${colSec.id}`}
              className="tx-heatmap__cell tx-heatmap__cell--diag"
              aria-label="self"
            />
          );
        }
        const pair = pairMap.get(`${rowSec.id}|${colSec.id}`);
        const j = toNumber(pair?.jaccardSimilarity);
        const bg = jaccardToColor(j);
        return (
          <div
            key={`${rowSec.id}-${colSec.id}`}
            className="tx-heatmap__cell"
            style={{ background: bg }}
            onMouseEnter={(e) => {
              onHoverCell({
                x: e.clientX,
                y: e.clientY,
                labelA: rowSec.displayName,
                labelB: colSec.displayName,
                jaccard: j,
                intersection: pair?.intersectionSize ?? 0,
                union: pair?.unionSize ?? 0,
              });
            }}
            onMouseMove={(e) => {
              onHoverCell({
                x: e.clientX,
                y: e.clientY,
                labelA: rowSec.displayName,
                labelB: colSec.displayName,
                jaccard: j,
                intersection: pair?.intersectionSize ?? 0,
                union: pair?.unionSize ?? 0,
              });
            }}
            onMouseLeave={() => onHoverCell(null)}
          />
        );
      })}
    </>
  );
}

// ── Drill-down 리스트 ─────────────────────────────────────────
// 특정 섹터가 포함된 쌍만 가져와서 상대 섹터 기준 jaccard 내림차순 Top-10.
function DrillDownList({
  sectorId,
  pairs,
}: {
  sectorId: string;
  pairs: SectorSimilarityPair[];
}): JSX.Element {
  const myPairs = pairs
    .filter((p) => p.sectorA === sectorId || p.sectorB === sectorId)
    .map((p) => {
      const otherId = p.sectorA === sectorId ? p.sectorB : p.sectorA;
      return {
        otherId,
        otherName: otherId, // display_name 별도 조회 없음
        jaccard: toNumber(p.jaccardSimilarity),
        intersection: p.intersectionSize,
        union: p.unionSize,
      };
    })
    .sort((a, b) => b.jaccard - a.jaccard)
    .slice(0, 10);

  return (
    <div className="tx-heatmap__drill">
      <h3 className="tx-heatmap__drill-title">
        <Info size={14} strokeWidth={1.75} /> <strong>{sectorId}</strong> 의 상위
        유사 섹터 (Top 10)
      </h3>
      <table className="pca-table">
        <thead>
          <tr>
            <th>순위</th>
            <th>섹터</th>
            <th>jaccard</th>
            <th>∩</th>
            <th>∪</th>
          </tr>
        </thead>
        <tbody>
          {myPairs.length === 0 && (
            <tr>
              <td colSpan={5} className="pca-muted">
                해당 섹터가 포함된 유사도 쌍이 없습니다.
              </td>
            </tr>
          )}
          {myPairs.map((p, idx) => (
            <tr key={p.otherId}>
              <td>{idx + 1}</td>
              <td>
                <strong>{p.otherName}</strong>
                <br />
                <span className="tx-tree__slug">{p.otherId}</span>
              </td>
              <td
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  background: jaccardToColor(p.jaccard),
                }}
              >
                {p.jaccard.toFixed(3)}
              </td>
              <td>{p.intersection}</td>
              <td>{p.union}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────
function HeatmapLegend(): JSX.Element {
  const stops = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
  return (
    <div className="tx-heatmap__legend">
      <span className="tx-heatmap__legend-label">jaccard:</span>
      <span className="tx-heatmap__legend-bar">
        {stops.map((v) => (
          <span
            key={v}
            className="tx-heatmap__legend-tick"
            style={{ background: jaccardToColor(v) }}
            title={v.toFixed(2)}
          />
        ))}
      </span>
      <span className="tx-heatmap__legend-label">0 → 1</span>
    </div>
  );
}

// ── 유틸 ───────────────────────────────────────────────────────
// 백엔드는 BigDecimal 을 문자열로 직렬화할 수도, number 로 직렬화할 수도 있다.
// 어느 쪽이든 안전하게 number 로 변환.
function toNumber(v: number | string | undefined | null): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === 'number') return v;
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : 0;
}

// ── 색 매핑 ────────────────────────────────────────────────────
// HSL 그라디언트: hue 는 210 (파랑) 고정, saturation+lightness 로 강도 조절.
// 0 = 흰색 (L=100%), 1 = 진한 파랑 (L=30%, S=85%).
function jaccardToColor(j: number): string {
  const v = Math.max(0, Math.min(1, j));
  const lightness = 100 - v * 70; // 100 → 30
  const saturation = v * 85;      // 0 → 85
  return `hsl(210, ${saturation.toFixed(0)}%, ${lightness.toFixed(0)}%)`;
}
